import * as THREE from 'three';
import { Raycaster, Intersection } from 'three';
import { FikoThreeJsComponent } from '../fiko-three-js.component';
import { FikoGrup } from './FikoGrup';
import { FikoSahne } from './FikoSahne';
import { FikoKutukComponent } from './fiko-kutuk/fiko-kutuk.component';
import { createRoughStockAround, RoughStockResult, RoughStockOptions } from './fiko-kutuk/fiko-kutuk.helper';
import { FikoSecililer } from './FikoSecililer';

export class FikoKutuk extends FikoGrup {
    override type: any = "FikoKutuk";

    odakAktif = false;
    secili = false;
    secilebilir = true;
    enUstte = true;

    // Kütük özellikleri
    roughStockResult: RoughStockResult | null = null;
    roughStockOptions: RoughStockOptions = {
        color: 0x999999,
        opacity: 0.5,
        alignToFace: null
    };
    secilenCizim: any = null;
    secilenFaceNormal: THREE.Vector3 | null = new THREE.Vector3(0, 0, 1); // Default Z ekseni
    hoverFaceNormalNormal: THREE.Vector3 | null = null; // Normal mod hover normal'ı

    // İki eksen seçim modu
    twoAxisMode: boolean = false;
    selectedAxes: THREE.Vector3[] = [];
    manualAxes: [THREE.Vector3, THREE.Vector3, THREE.Vector3] | null = null;
    hoverFaceNormal: THREE.Vector3 | null = null; // Hover'da saklanan normal

    // FikoSecililer - yüzey seçimi için
    fikoSecimYuzey: FikoSecililer | null = null;

    renkler = {
        DEFAULT: 0x999999,
        ODAK: 0xFF7F00,
        SECIM: 0xff0000,
    }

    sagMenu = [
        {
            simgeYazi: "👁‍🗨",
            baslik: "Gizle / Göster",
            tiklandi: () => {
                this.visible = !this.visible;
                this.sahne.anaGosterim.FikoYT.degisti.emit();
            }
        },
        {
            simgeYazi: "🖥",
            baslik: "Ekrana Sığdır",
            tiklandi: () => {
                this.sahne.ekranaSigdir(this.sahne);
            }
        },
        {
            simgeYazi: "🖊",
            baslik: "Düzenle",
            tiklandi: () => {
                this.sahne.anaGosterim.DialogFiko.ac({ bilesen: FikoKutukComponent, parametreler: { kutuk: this }, $event: null, ayarlar: { baslik: 'Kütük Ayarları', simgeResim: './simgeler/kupler.webp' } });
            }
        },
    ]

    override solAracCubugu = [
        {
            baslik: "Düzenle",
            simge: "edit",
            tiklandi: () => {
                this.sahne.anaGosterim.DialogFiko.ac({ bilesen: FikoKutukComponent, parametreler: { kutuk: this }, $event: null, ayarlar: { baslik: 'Kütük Ayarları', simgeResim: './simgeler/kupler.webp' } });
            }
        }
    ]

    hizli3DMenu = [
        {
            simgeYazi: "👁‍🗨",
            baslik: "Gizle / Göster",
            tiklandi: () => {
                this.visible = !this.visible;
                this.sahne.anaGosterim.FikoYT.degisti.emit();
            }
        },
    ]
    hizliMenu3DBilgi?: string = "Kütük";

    constructor(public override sahne?: FikoSahne, name?: string) {
        super(sahne, name);
        // FIXED: Stock should render BEHIND drawing mesh (which has renderOrder -2)
        // Stock's default renderOrder was 0, placing it on TOP of drawing
        this.renderOrder = -3;
        this.userData.FikoArayuz.agactaGorunur = true;
        this.userData.FikoArayuz.listeGoster = false;
        this.userData.FikoArayuz.simge = "./simgeler/kupler.webp";

        if (sahne)
            this.hazirla(sahne);
    }

    override hazirla(sahne?: FikoSahne) {
        super.hazirla(sahne);
    }

    /**
     * Cizim seç ve ona göre Kütük oluştur
     */
    cizimSec(cizim: any) {
        this.secilenCizim = cizim;
        this.roughStockOlusturCizimIcin();
    }

    /**
     * Bu kutuk'un sahibi olan FikoCizimYalin'deki tüm cizimler döndür
     * FikoCizimYalin > FikoKutuk > (burası)
     */
    getCizimler() {
        if (!this.parent || !this.parent.parent) return [];

        // Parent: FikoCizimYalin
        const cizim = this.parent as any;

        // FikoCizimYalin'in children'larından mesh, cizgiler, noktalar haricini döndür
        return cizim.children.filter((child: any) => {
            // Kendi FikoKutuk'u hariç tut
            if (child === this) return false;
            // Konumlar, işaretleyiciler gibi sistem elemanlarını hariç tut
            if (child.type === 'FikoKonumlar' || child.type === 'Fiko3DIsaretleyiciler') return false;
            // Mesh, Points gibi Three.js objelerini hariç tut
            if (child.type === 'Mesh' || child.type === 'Points' || child.type === 'LineSegments') return false;
            // Kalan FikoGrupCizim'leri döndür (varsa)
            return child.type === 'FikoGrupCizim';
        });
    }

    /**
     * Kütük oluştur
     * CATIA'daki "Create Kütük" benzeri işlem
     */
    roughStockOlustur() {
        if (!this.parent) {
            return;
        }

        // Bu grup için Kütük oluştur
        this.roughStockResult = createRoughStockAround(this, this.roughStockOptions);

        if (this.roughStockResult) {
            // Stock'u bu grubun içine ekle
            this.add(this.roughStockResult.stock);
        }

        return this.roughStockResult;
    }

    /**
     * Seçilen cizim (veya parent/sahibi cizim) için Kütük oluştur
     * Face normal seçildiyse, kütüğü o yüzeye göre hizala
     */
    roughStockOlusturCizimIcin() {
        // Seçilen cizim varsa onu kullan, yoksa parent (FikoCizimYalin) kullan
        const hedefCizim = this.secilenCizim || (this.parent as any);

        if (!hedefCizim) {
            return;
        }

        // Eski stock varsa kaldır
        if (this.roughStockResult && this.roughStockResult.stock.parent) {
            this.remove(this.roughStockResult.stock);
        }

        // Seçilen yüzün normali ile options'u güncelle
        const optionsWithFace: RoughStockOptions = {
            color: this.roughStockOptions.color,
            opacity: this.roughStockOptions.opacity,
            alignToFace: this.secilenFaceNormal // Asıl çizim yüzünün normali
        };

        // Seçilen/parent cizim için Kütük oluştur
        this.roughStockResult = createRoughStockAround(hedefCizim, optionsWithFace);

        if (this.roughStockResult) {
            // Stock'u bu grubun içine ekle
            this.add(this.roughStockResult.stock);
        }

        return this.roughStockResult;
    }

    /**
     * Renk güncelle
     */
    renkGuncelle(newColor: THREE.ColorRepresentation) {
        this.roughStockOptions.color = newColor;
        if (this.roughStockResult) {
            const fillMesh = this.roughStockResult.stock.children[0] as THREE.Mesh;
            if (fillMesh && fillMesh.material) {
                (fillMesh.material as THREE.MeshStandardMaterial).color.set(newColor);
            }
            const edges = this.roughStockResult.stock.children[1] as THREE.LineSegments;
            if (edges && edges.material) {
                (edges.material as THREE.LineBasicMaterial).color.set(newColor);
            }
        }
    }

    /**
     * Opacity güncelle
     */
    opacityGuncelle(newOpacity: number) {
        this.roughStockOptions.opacity = newOpacity;
        if (this.roughStockResult) {
            const fillMesh = this.roughStockResult.stock.children[0] as THREE.Mesh;
            if (fillMesh && fillMesh.material) {
                (fillMesh.material as THREE.MeshStandardMaterial).opacity = newOpacity;
            }
        }
    }

    /**
     * Stock'u grubundan kaldır
     */
    stockuKaldir() {
        if (this.roughStockResult && this.roughStockResult.stock.parent) {
            this.remove(this.roughStockResult.stock);
            this.roughStockResult = null;
        }
    }

    odakta() {
        this.odakAktif = true;
        // if (this.secili) return;
        // if (this.roughStockResult) {
        //     const fillMesh = this.roughStockResult.stock.children[0] as THREE.Mesh;
        //     if (fillMesh && fillMesh.material) {
        //         (fillMesh.material as THREE.MeshStandardMaterial).color.set(this.renkler.ODAK);
        //     }
        //     const edges = this.roughStockResult.stock.children[1] as THREE.LineSegments;
        //     if (edges && edges.material) {
        //         (edges.material as THREE.LineBasicMaterial).color.set(this.renkler.ODAK);
        //     }
        // }
    }

    odaktaDegil() {
        this.odakAktif = false;
        // if (this.secili) return;
        // if (this.roughStockResult) {
        //     const fillMesh = this.roughStockResult.stock.children[0] as THREE.Mesh;
        //     if (fillMesh && fillMesh.material) {
        //         (fillMesh.material as THREE.MeshStandardMaterial).color.set(this.roughStockOptions.color);
        //     }
        //     const edges = this.roughStockResult.stock.children[1] as THREE.LineSegments;
        //     if (edges && edges.material) {
        //         (edges.material as THREE.LineBasicMaterial).color.set(this.roughStockOptions.color);
        //     }
        // }
    }

    secildi() {
        // if (this.roughStockResult) {
        //     const fillMesh = this.roughStockResult.stock.children[0] as THREE.Mesh;
        //     if (fillMesh && fillMesh.material) {
        //         (fillMesh.material as THREE.MeshStandardMaterial).color.set(this.renkler.SECIM);
        //     }
        //     const edges = this.roughStockResult.stock.children[1] as THREE.LineSegments;
        //     if (edges && edges.material) {
        //         (edges.material as THREE.LineBasicMaterial).color.set(this.renkler.SECIM);
        //     }
        // }
        this.secili = true;
    }

    secilmedi() {
        // if (this.roughStockResult) {
        //     const fillMesh = this.roughStockResult.stock.children[0] as THREE.Mesh;
        //     if (fillMesh && fillMesh.material) {
        //         (fillMesh.material as THREE.MeshStandardMaterial).color.set(this.roughStockOptions.color);
        //     }
        //     const edges = this.roughStockResult.stock.children[1] as THREE.LineSegments;
        //     if (edges && edges.material) {
        //         (edges.material as THREE.LineBasicMaterial).color.set(this.roughStockOptions.color);
        //     }
        // }
        this.secili = false;
    }

    // Artık FikoSecililer kullanıldığı için raycast override'a gerek yok
    // override raycast() kaldırıldı - yüzey seçimi FikoSecililer ile yapılıyor

    /**
     * Yüzey seçimini başlat (FikoSecililer ile)
     */
    yuzeySecimiBaslat() {
        if (!this.sahne?.anaGosterim) return;

        // Zaten aktifse tekrar başlatma
        if (this.fikoSecimYuzey?.aktif) {
            return;
        }

        // Parent mesh kontrolü
        const parentCizim = this.parent as any;
        if (!parentCizim?.mesh) {
            return;
        }

        // Önceki FikoSecililer varsa temizle
        if (this.fikoSecimYuzey) {
            this.fikoSecimYuzey.bitir();
            this.fikoSecimYuzey = null;
        }

        // FikoSecililer oluştur
        this.fikoSecimYuzey = new FikoSecililer(this.sahne.anaGosterim);

        // Parent mesh'i rayObje olarak ayarla
        this.fikoSecimYuzey.rayObje = [parentCizim.mesh];
        this.fikoSecimYuzey.filitreler = ["Mesh"];

        // Raycast params ayarla - face-level raycast için
        this.fikoSecimYuzey.raycaster.params.Mesh = { threshold: 0 };

        // Tıklama event'ini dinle
        this.fikoSecimYuzey.tiklandi.subscribe((kesisim: any) => {

            // Boş alana tıklanmışsa (kesişim yok) - çık
            if (!kesisim || !kesisim.object) {
                return;
            }

            // Kesişim noktasındaki face normal'ı direkt al
            // Uyumluluk: face.normal deprecated, geometry normal'ından hesapla
            let faceNormal: THREE.Vector3 | null = null;
            const actualMesh = kesisim?.altObje || kesisim?.object;
            const mesh = actualMesh as THREE.Mesh;

            if (kesisim?.face?.normal) {
                // Eski API: face.normal (deprecated)
                const localNormal = kesisim.face.normal.clone();
                faceNormal = localNormal.applyMatrix3(
                    new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld)
                ).normalize();
            } else if (mesh.geometry && kesisim.faceIndex !== undefined) {
                // Yeni API: geometry normal'ından hesapla
                const geometry = mesh.geometry as THREE.BufferGeometry;
                const normalAttr = geometry.getAttribute('normal') as THREE.BufferAttribute;
                if (normalAttr) {
                    const faceIndex = kesisim.faceIndex;
                    const normalArray = new Float32Array(3);
                    normalArray[0] = normalAttr.getX(faceIndex);
                    normalArray[1] = normalAttr.getY(faceIndex);
                    normalArray[2] = normalAttr.getZ(faceIndex);
                    const localNormal = new THREE.Vector3(normalArray[0], normalArray[1], normalArray[2]);
                    faceNormal = localNormal.applyMatrix3(
                        new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld)
                    ).normalize();
                }
            }

            if (faceNormal) {

                if (this.twoAxisMode) {
                    // 2 Eksen modu - eksen ekle
                    this.addSelectedAxis(faceNormal);

                    // İki eksen seçildiyse yüzey seçimini durdur
                    if (this.selectedAxes.length === 2) {
                        this.yuzeySecimiDurdur();
                        // ✅ Menü kapatma engeli kaldır
                        const parentCizim = this.parent as any;
                        if (parentCizim?.kapatmayiEngelle !== undefined) {
                            parentCizim.kapatmayiEngelle = false;
                        }
                    }
                } else {
                    // Normal mod - yüzey seç
                    this.secilenFaceNormal = faceNormal;
                    this.roughStockOlusturCizimIcin();

                    // Yüzey seçildi, yüzey seçimini durdur
                    this.yuzeySecimiDurdur();
                    // ✅ Menü kapatma engeli kaldır
                    const parentCizim = this.parent as any;
                    if (parentCizim?.kapatmayiEngelle !== undefined) {
                        parentCizim.kapatmayiEngelle = false;
                    }
                }

                this.sahne.anaGosterim.FikoYT.degisti.emit();
            } else {
            }
        });

        // Aktifleştir
        this.fikoSecimYuzey.baslat();
        this.fikoSecimYuzey.aktif = true;
    }

    /**
     * Yüzey seçimini durdur
     */
    yuzeySecimiDurdur() {
        if (this.fikoSecimYuzey) {
            this.fikoSecimYuzey.aktif = false;
            this.fikoSecimYuzey.bitir();
            this.fikoSecimYuzey = null;
        }
    }

    /**
     * Eksen seçimini sıfırla
     */
    resetAxisSelection() {
        this.selectedAxes = [];
        this.manualAxes = null;
        this.sahne.anaGosterim.FikoYT.degisti.emit();
    }

    /**
     * Eksen ekle ve iki eksen seçildiyse kütüğü güncelle
     * İkinci eksen birinciye dik değilse, Gram-Schmidt ile dik hale getir
     */
    addSelectedAxis(axis: THREE.Vector3) {
        if (this.selectedAxes.length < 2) {
            let normalizedAxis = axis.clone().normalize();

            // İkinci eksen seçiliyorsa, birinciye dik olacak şekilde ayarla
            if (this.selectedAxes.length === 1) {
                const X = this.selectedAxes[0];
                const Y_input = normalizedAxis;

                // Gram-Schmidt: Y'yi X'e dik kıl
                // Y = Y_input - (Y_input · X) * X
                const dotProduct = Y_input.dot(X);
                normalizedAxis = Y_input.clone()
                    .addScaledVector(X, -dotProduct)
                    .normalize();

                // Eğer iki vektör paralelse (normalize edilen sonuç sıfır vektör), uyarı ver
                if (!isFinite(normalizedAxis.x) || !isFinite(normalizedAxis.y) || !isFinite(normalizedAxis.z)) {
                    return;
                }
            }

            this.selectedAxes.push(normalizedAxis);

            // İki eksen seçildiyse üçüncüyü hesapla
            if (this.selectedAxes.length === 2) {
                const X = this.selectedAxes[0];
                const Y = this.selectedAxes[1];
                // Z = X × Y (right-hand rule)
                const Z = new THREE.Vector3().crossVectors(X, Y).normalize();

                this.manualAxes = [X, Y, Z];

                // Stock yoksa oluştur, varsa güncelle
                if (!this.roughStockResult) {
                    // İlk kez oluşturuluyorsa, normal stock oluştur
                    this.roughStockOlustur();
                }

                // Kütüğü yeni eksenlere göre güncelle
                this.roughStockOlusturManualAxes();
                this.sahne.anaGosterim.FikoYT.degisti.emit();
            }
        }
    }

    /**
     * Manuel eksenlerle stock oluştur (Z ekseni face aligned)
     */
    roughStockOlusturManualAxes() {
        if (!this.manualAxes || !this.roughStockResult) return;

        const hedefCizim = this.secilenCizim || (this.parent as any);
        if (!hedefCizim) return;

        // Eski stock varsa kaldır
        if (this.roughStockResult.stock.parent) {
            this.remove(this.roughStockResult.stock);
        }

        // Manuel eksenlerle face-aligned stock oluştur
        // Z ekseni face normal olarak verilir, böylece helper face alignment yapacak
        const optionsWithFace: RoughStockOptions = {
            color: this.roughStockOptions.color,
            opacity: this.roughStockOptions.opacity,
            alignToFace: this.manualAxes[2] // Z ekseni (hesaplanan 3. eksen) face normal olarak
        };

        // AABB'den stock oluştur
        const box = new THREE.Box3().setFromObject(hedefCizim);
        const center = new THREE.Vector3();
        box.getCenter(center);
        const size = new THREE.Vector3();
        box.getSize(size);

        // Manuel eksenlere göre min-max hesapla
        const X = this.manualAxes[0];
        const Y = this.manualAxes[1];
        const Z = this.manualAxes[2];

        let minX = Infinity, maxX = -Infinity;
        let minY = Infinity, maxY = -Infinity;
        let minZ = Infinity, maxZ = -Infinity;

        // Parent mesh'in vertices'ini tarayıp manuel eksenlere göre min-max bul
        const parentCizim = hedefCizim as any;
        if (parentCizim.mesh && parentCizim.mesh.geometry) {
            const geometry = parentCizim.mesh.geometry as THREE.BufferGeometry;
            const positionAttribute = geometry.getAttribute('position');

            if (positionAttribute) {
                const positions = positionAttribute.array as Float32Array;

                for (let i = 0; i < positions.length; i += 3) {
                    const vertex = new THREE.Vector3(positions[i], positions[i + 1], positions[i + 2]);
                    vertex.applyMatrix4(parentCizim.mesh.matrixWorld);

                    const projX = vertex.dot(X);
                    const projY = vertex.dot(Y);
                    const projZ = vertex.dot(Z);

                    minX = Math.min(minX, projX);
                    maxX = Math.max(maxX, projX);
                    minY = Math.min(minY, projY);
                    maxY = Math.max(maxY, projY);
                    minZ = Math.min(minZ, projZ);
                    maxZ = Math.max(maxZ, projZ);
                }
            }
        }

        // Boyutları ve merkezi hesapla
        const sizeX = maxX - minX;
        const sizeY = maxY - minY;
        const sizeZ = maxZ - minZ;

        const newCoordsCenterX = (minX + maxX) / 2;
        const newCoordsCenterY = (minY + maxY) / 2;
        const newCoordsCenterZ = (minZ + maxZ) / 2;

        // Merkezi world koordinatlarına çevir
        const newCenter = new THREE.Vector3(0, 0, 0);
        newCenter.addScaledVector(X, newCoordsCenterX);
        newCenter.addScaledVector(Y, newCoordsCenterY);
        newCenter.addScaledVector(Z, newCoordsCenterZ);

        // Stock oluştur
        const axes: [THREE.Vector3, THREE.Vector3, THREE.Vector3] = [X, Y, Z];
        const stockGeom = new THREE.BoxGeometry(sizeX, sizeY, sizeZ);
        const stockMat = new THREE.MeshStandardMaterial({
            color: this.roughStockOptions.color,
            transparent: true,
            opacity: this.roughStockOptions.opacity,
            depthWrite: false  // Arkasındaki highlight'ların görünmesi için
        });
        const fillMesh = new THREE.Mesh(stockGeom, stockMat);

        // 2 Eksen modunda stock raycast edilemez (sadece görsel)
        fillMesh.raycast = () => {};

        // Edges
        const edgeGeom = new THREE.EdgesGeometry(stockGeom);
        const edgeMat = new THREE.LineBasicMaterial({ color: this.roughStockOptions.color, linewidth: 2 });
        const edges = new THREE.LineSegments(edgeGeom, edgeMat);

        // Edges de raycast edilemez
        edges.raycast = () => {};

        // Group oluştur
        const stock = new THREE.Group();
        stock.add(fillMesh);
        stock.add(edges);

        stock.position.copy(newCenter);
        stock.setRotationFromMatrix(new THREE.Matrix4().makeBasis(axes[0], axes[1], axes[2]));

        this.roughStockResult = {
            stock: stock,
            sizes: new THREE.Vector3(sizeX, sizeY, sizeZ),
            center: newCenter,
            axes: axes
        };

        this.add(this.roughStockResult.stock);
    }

    ciftTiklandi($event?) {
        this.sahne.anaGosterim.DialogFiko.ac({ bilesen: FikoKutukComponent, parametreler: { kutuk: this }, $event: $event, ayarlar: { baslik: 'Kütük Ayarları', simgeResim: './simgeler/kupler.webp' } });
    }

    override toJSON(meta?: { geometries: any; materials: any; textures: any; images: any }): any {
        const _rslt: any = {
            type: this.type,
            isim: this.name,
            uuid: this.uuid,
            matrix: this.matrix.toArray(),
            visible: this.visible,
            userData: this._serializeUserData(),
            roughStockOptions: this.roughStockOptions,
        };

        return _rslt;
    }

    static fromJson(sahne?: FikoSahne, parent?: any, json?: any) {
        const _rslt = new FikoKutuk(sahne, json.isim);

        if (json.roughStockOptions) {
            _rslt.roughStockOptions = json.roughStockOptions;
        }
        if (json.uuid) _rslt.uuid = json.uuid;
        if (json.userData) _rslt._restoreUserData(json.userData);
        if (json.visible !== undefined) _rslt.visible = json.visible;
        if (json.matrix) {
            _rslt.matrix.fromArray(json.matrix);
            _rslt.matrix.decompose(_rslt.position, _rslt.quaternion, _rslt.scale);
        }

        _rslt.parent = parent;
        return _rslt;
    }
}
